
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Loader2 } from 'lucide-react';
import ArticleCard from './shared/ArticleCard';
import EmptyState from './shared/EmptyState';
import { Article } from '@/hooks/journalist/types';
import { useAuth } from '@/hooks/useAuth';
import { useParams } from 'react-router-dom';
import { checkJournalistSubscription, createJournalistCheckoutSession } from '@/services/stripeService';
import { toast } from "@/components/ui/use-toast";

interface PremiumContentTabProps {
  articles: Article[];
}

const PremiumContentTab: React.FC<PremiumContentTabProps> = ({ articles }) => {
  const { userProfile } = useAuth();
  const { journalistId } = useParams<{ journalistId: string }>();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  
  // Filter premium articles
  const premiumArticles = articles.filter(article => article.premium);
  
  // Safely determine if this is the user's own profile
  const isOwnProfile = userProfile?.id === journalistId;
  
  useEffect(() => {
    const checkAccess = async () => {
      if (journalistId && !isOwnProfile) {
        const { hasAccess } = await checkJournalistSubscription(journalistId);
        setHasAccess(hasAccess);
      } else if (isOwnProfile) {
        setHasAccess(true);
      }
      setLoading(false);
    };
    
    checkAccess();
  }, [journalistId, isOwnProfile]);
  
  const handleSubscribe = async () => {
    if (!journalistId) return;
    
    try {
      setSubscribing(true);
      
      // Create a price ID for this journalist's subscription
      // In a real implementation, this would be stored in the database
      const priceId = `price_journalist_${journalistId}`;
      
      const checkoutUrl = await createJournalistCheckoutSession(journalistId, priceId);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      console.error('Error starting subscription:', error);
      toast({
        variant: 'destructive',
        title: 'Subscription error',
        description: 'There was a problem processing your subscription. Please try again.'
      });
      setSubscribing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Premium Content</h2>
        {!isOwnProfile && !hasAccess && (
          <Button 
            size="sm"
            onClick={handleSubscribe}
            disabled={subscribing}
          >
            {subscribing ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : null}
            Subscribe to Access
          </Button>
        )}
      </div>
      
      <div className="grid grid-cols-1 gap-6">
        {hasAccess ? (
          premiumArticles.length > 0 ? (
            premiumArticles.map(article => (
              <ArticleCard 
                key={article.id} 
                article={article} 
                layout="horizontal"
              />
            ))
          ) : (
            <EmptyState 
              message={isOwnProfile ? 
                "You don't have any premium articles yet." : 
                "No premium articles available."}
              showDefaultAction={isOwnProfile}
            />
          )
        ) : (
          <div className="bg-muted/50 rounded-lg p-6 text-center">
            <h3 className="text-lg font-medium mb-2">Premium Content Locked</h3>
            <p className="text-muted-foreground mb-4">
              Subscribe to this journalist to access their premium articles and exclusive content.
            </p>
            <Button onClick={handleSubscribe} disabled={subscribing}>
              {subscribing ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : null}
              Subscribe Now
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default PremiumContentTab;
